import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import block from 'bem-cn'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'
import { ROUTE_URLS } from 'router/routes'

import { ReactComponent as Logout } from 'shared/img/logout.svg'
import refreshIcon from 'shared/img/refresh.png'

import { LocaleProfileKeysType } from 'shared/locale/en-US'
import { useMediaDevice, useModalContext } from '@bcraft-frontend-lib/core'

import { actions as authActions } from 'features/auth'
import { selectUserData } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'
import { selectSettings } from 'features/settings/selectors'

import { sidemenuHistoryItems, sidemenuSettingsItems } from 'components/NavigationHeader/data'

import { ReactComponent as CloseIcon } from '../img/close.svg'
import { ReactComponent as HistoryIcon } from './img/history.svg'
import { ReactComponent as SettingsIcon } from './img/settings.svg'

import './SideMenuRightContent.scss'

const b = block('side-menu-right-content')

export const SideMenuRightContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { pushNewModal } = useModalContext()
  const dispatch = useDispatch()
  const locale = useSelector(selectLocaleDict, shallowEqual).profile
  const user = useSelector(selectUserData, shallowEqual)
  const isBonusTransferAvailable =
    useSelector(selectSettings, shallowEqual).settings.bonuses.transferBonusBalanceToBalanceOn === true
  const { pathname, search } = useLocation()
  const isMobileOrTablet = useMediaDevice({ query: 'maxTablet' })

  const visibleSettingsItems = useMemo(
    () =>
      sidemenuSettingsItems.filter(
        item => item.route !== ROUTE_URLS.PROFILE_EXCHANGE_BONUS_BALANCE() || isBonusTransferAvailable,
      ),
    [isBonusTransferAvailable],
  )

  const handleLogout = useCallback(() => {
    onClose()
    pushNewModal(MODAL_ROUTE_URLS.LOGOUT())()
  }, [onClose, pushNewModal])

  const handleRefreshBalance = useCallback(() => {
    dispatch(authActions.getUserData())
  }, [dispatch])

  return (
    <div className={b()}>
      <div className={b('header')}>
        <div className={b('header-title')}>
          <span className={b('header-text')}>{locale.accountMenu}</span>
        </div>
        <button onClick={onClose} type="button" className={b('header-close')} aria-label="Close menu">
          <CloseIcon />
        </button>
      </div>

      <div className={b('body')}>
        <div className={b('body-top')}>
          <div className={b('balance')}>
            <div className={b('balance-row')}>
              <span className={b('balance-title')}>{locale.balance}:</span>
              <div className={b('balance-value')}>
                <span className={b('balance-amount')}>{user?.balance ?? '0.00'}</span>
                <span className={b('balance-currency')}>{user?.currency}</span>
                <button
                  className={b('balance-refresh')}
                  type="button"
                  onClick={handleRefreshBalance}
                  aria-label="Refresh balance">
                  <img className={b('balance-refresh-icon')} src={refreshIcon} alt="" aria-hidden />
                </button>
              </div>
            </div>
            <div className={b('balance-row')}>
              <span className={b('balance-title')}>{locale.bonus}:</span>
              <div className={b('balance-value')}>
                <span className={b('balance-amount')}>{user?.bonusBalance.toFixed(2) ?? '0.00'}</span>
                <span className={b('balance-currency')}>B</span>
                <button
                  className={b('balance-refresh')}
                  type="button"
                  onClick={handleRefreshBalance}
                  aria-label="Refresh balance">
                  <img className={b('balance-refresh-icon')} src={refreshIcon} alt="" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={b('list')}>
          <div className={b('list-title')}>
            <HistoryIcon />
            <span className={b('list-title-text')}>{locale.history}</span>
          </div>
          <div className={b('list-content')}>
            {sidemenuHistoryItems.map(item => {
              const isActive = pathname === item.route || !!item.subItems?.find(it => `${pathname}${search}` === it)
              return (
                <Link
                  key={item.text}
                  onClick={onClose}
                  className={b('menu-item', { active: isActive })}
                  to={isMobileOrTablet ? item.mobileRoute ?? item.route : item.route}>
                  <span className={b('menu-item-text')}>{locale?.[item.text as LocaleProfileKeysType]}</span>
                </Link>
              )
            })}
          </div>
        </div>

        <div className={b('divider')} />

        <div className={b('list')}>
          <div className={b('list-title')}>
            <SettingsIcon />
            <span className={b('list-title-text')}>{locale.settings}</span>
          </div>
          <div className={b('list-content')}>
            {visibleSettingsItems.map(item => {
              const isActive = pathname === item.route || !!item.subItems?.find(it => `${pathname}${search}` === it)
              return (
                <Link
                  key={item.text}
                  onClick={onClose}
                  className={b('menu-item', { active: isActive })}
                  to={isMobileOrTablet ? item.mobileRoute ?? item.route : item.route}>
                  <span className={b('menu-item-text')}>{locale?.[item.text as LocaleProfileKeysType]}</span>
                </Link>
              )
            })}
          </div>
        </div>

        <button className={b('logout')} onClick={handleLogout}>
          <Logout />
        </button>
      </div>
    </div>
  )
}
